"use strict";
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const React = __importStar(require("react"));
const Interactable_1 = require("./Interactable");
const SpaceContext_1 = require("./SpaceContext");
const utils_1 = require("./utils");
const DEFAULT_LONG_TAP_THRESHOLD_MS = 500;
/**
 * Works like a button element except tapping does not prevent or conflict with
 * panning. It can also recognize long taps, and can even capture the press
 * entirely if you want to implementing something like dragging.
 *
 * It does not provide any UI.
 *
 * Must only be used inside a `Space`.
 *
 * ## Props
 *
 * See `PressableProps`.
 */
class Pressable extends React.PureComponent {
    constructor() {
        super(...arguments);
        this.divRef = React.createRef();
        this.id = utils_1.generateRandomId();
        this.state = { interaction: undefined, hovered: false };
        this.determineClassName = () => {
            const { className } = this.props;
            let result = 'react-zoomable-ui-pressable';
            if (className) {
                result += ' ';
                result += className;
            }
            if (this.props.disabled) {
                if (this.props.disabledClassName) {
                    result += ' ';
                    result += this.props.disabledClassName;
                }
            }
            else if (this.state.interaction === 'potential-tap') {
                if (this.props.potentialTapClassName) {
                    result += ' ';
                    result += this.props.potentialTapClassName;
                }
            }
            else if (this.state.interaction === 'potential-long-tap') {
                if (this.props.potentialLongTapClassName) {
                    result += ' ';
                    result += this.props.potentialLongTapClassName;
                }
            }
            else if (this.state.interaction === 'press-captured') {
                if (this.props.capturePressClassName) {
                    result += ' ';
                    result += this.props.capturePressClassName;
                }
            }
            else if (this.state.hovered) {
                if (this.props.hoverClassName) {
                    result += ' ';
                    result += this.props.hoverClassName;
                }
            }
            return result;
        };
        this.determineStyle = () => {
            const { style } = this.props;
            if (this.props.disabled) {
                if (this.props.disabledStyle) {
                    return Object.assign(Object.assign({}, (style || {})), this.props.disabledStyle);
                }
            }
            else if (this.state.interaction === 'potential-tap') {
                if (this.props.potentialTapStyle) {
                    return Object.assign(Object.assign({}, (style || {})), this.props.potentialTapStyle);
                }
            }
            else if (this.state.interaction === 'potential-long-tap') {
                if (this.props.potentialLongTapStyle) {
                    return Object.assign(Object.assign({}, (style || {})), this.props.potentialLongTapStyle);
                }
            }
            else if (this.state.interaction === 'press-captured') {
                if (this.props.capturePressStyle) {
                    return Object.assign(Object.assign({}, (style || {})), this.props.capturePressStyle);
                }
            }
            else if (this.state.hovered) {
                if (this.props.hoverStyle) {
                    return Object.assign(Object.assign({}, (style || {})), this.props.hoverStyle);
                }
            }
            return style;
        };
        this.handleCapturePressStart = (coordinates) => {
            var _a, _b;
            this.setState({ interaction: 'press-captured' });
            this.panStartingCoordinates = coordinates;
            if (this.divRef.current) {
                (_b = (_a = this.props).onCapturePressStart) === null || _b === void 0 ? void 0 : _b.call(_a, coordinates, this.divRef.current);
            }
        };
        this.handleCapturePressMove = (coordinates) => {
            var _a, _b;
            if (this.divRef.current && this.panStartingCoordinates) {
                (_b = (_a = this.props).onCapturePressMove) === null || _b === void 0 ? void 0 : _b.call(_a, coordinates, this.divRef.current, this.panStartingCoordinates);
            }
        };
        this.handleCapturePressEnd = (coordinates) => {
            var _a, _b;
            this.setState({ interaction: undefined });
            this.panStartingCoordinates = undefined;
            if (this.divRef.current) {
                (_b = (_a = this.props).onCapturePressEnd) === null || _b === void 0 ? void 0 : _b.call(_a, coordinates, this.divRef.current);
            }
        };
        this.handleCapturePressCancelled = () => {
            var _a, _b;
            this.setState({ interaction: undefined });
            this.panStartingCoordinates = undefined;
            if (this.divRef.current) {
                (_b = (_a = this.props).onCapturePressCancelled) === null || _b === void 0 ? void 0 : _b.call(_a, this.divRef.current);
            }
        };
        this.handleTapAbandoned = () => {
            this.setState({ interaction: undefined });
        };
        this.handleLongTap = () => {
            var _a, _b;
            this.setState({ interaction: undefined });
            (_b = (_a = this.props).onLongTap) === null || _b === void 0 ? void 0 : _b.call(_a);
        };
        this.handlePotentialLongTap = () => {
            this.setState({ interaction: 'potential-long-tap' });
        };
        this.handlePotentialTap = () => {
            this.setState({ interaction: 'potential-tap' });
        };
        this.handleTap = () => {
            var _a, _b;
            this.setState({ interaction: undefined });
            (_b = (_a = this.props).onTap) === null || _b === void 0 ? void 0 : _b.call(_a);
        };
    }
    componentDidMount() {
        this.context.registerInteractable(this);
    }
    componentWillUnmount() {
        this.context.unregisterInteractable(this);
    }
    getPressHandlingConfig() {
        var _a;
        if (this.props.disabled) {
            return undefined;
        }
        return {
            onPotentialTap: this.handlePotentialTap,
            onTap: this.handleTap,
            longTapThresholdMs: this.props.onLongTap
                ? (_a = this.props.longTapThresholdMs, (_a !== null && _a !== void 0 ? _a : DEFAULT_LONG_TAP_THRESHOLD_MS)) : undefined,
            onPotentialLongTap: this.handlePotentialLongTap,
            onLongTap: this.handleLongTap,
            onTapAbandoned: this.handleTapAbandoned,
            capturePressThresholdMs: this.props.capturePressThresholdMs,
            onCapturePressStart: this.handleCapturePressStart,
            onCapturePressMove: this.handleCapturePressMove,
            onCapturePressEnd: this.handleCapturePressEnd,
            onCapturePressCancelled: this.handleCapturePressCancelled,
        };
    }
    render() {
        return (React.createElement("div", Object.assign({}, { [Interactable_1.InteractableIdAttributeName]: this.id }, { id: this.props.id, ref: this.divRef, className: this.determineClassName(), style: this.determineStyle() }), typeof this.props.children === 'function' ? this.props.children(this.state) : this.props.children));
    }
    /**
     * Called by the `Space` to change the hover state.
     *
     * This isn't intended to be called from outside the library.
     */
    setHovered(hovered) {
        this.setState({ hovered });
    }
}
exports.Pressable = Pressable;
Pressable.contextType = SpaceContext_1.SpaceContext;
//# sourceMappingURL=Pressable.js.map